import { Howl } from 'howler';
import { debounce, DebouncedFunc } from 'lodash';
import unitTimersAlert from '../assets/sounds/unit_timers_alert.mp3';
import aedAttemptingAccepting from '../assets/sounds/aed_attempting_accepting.mp3';
import dexHotHitAlert from '../assets/sounds/dex_hot_hit_notification.mp3';
import newMessageReceived from '../assets/sounds/msg_ding.mp3';
import firstResponderIncomingMsg from '../assets/sounds/beep.mp3';
import emergencySiren from '../assets/sounds/emergency_siren.mp3';
import incomingBolo from '../assets/sounds/incoming_bolo_alert.mp3';
import draftEventTimersAlert from '../assets/sounds/draft_event_timers_alert.mp3';
import newEventAlert from '../assets/sounds/new_event.mp3';

export type SoundName =
    | 'unit-timer-alert'
    | 'aed-attempting-accepting'
    | 'dex-hot-hit-alert'
    | 'new-message-received'
    | 'first-responder-incoming-msg'
    | 'first-responder-incoming-bolo'
    | 'emergency-siren'
    | 'draft-event-timer-alert'
    | 'new-event-alert';

// Exported so the asset smoke test can load every bundled file in a real browser.
export const soundSources: Record<SoundName, string> = {
    'unit-timer-alert': unitTimersAlert,
    'aed-attempting-accepting': aedAttemptingAccepting,
    'dex-hot-hit-alert': dexHotHitAlert,
    'new-message-received': newMessageReceived,
    'first-responder-incoming-msg': firstResponderIncomingMsg,
    'first-responder-incoming-bolo': incomingBolo,
    'emergency-siren': emergencySiren,
    'draft-event-timer-alert': draftEventTimersAlert,
    'new-event-alert': newEventAlert,
};

/**
 * Plays sounds on independent channels. Each channel has its own Howls, debounces and loop, so
 * nothing one channel does (a debounce, a stop, a loop) reaches a sound playing on another, even the
 * same sound. Howler's play/stop/loop act on every instance of a Howl, hence a Howl per channel.
 * Only the audible alerts module plays sounds, one channel per alert; this is its seam for tests.
 */
export interface SoundPlayer {
    /** Debounced (1 s, leading) per channel and sound, and skipped while the channel is playing it. */
    playSound: (soundName: SoundName, channel: string) => void;
    /** Stops the channel's one-shot sounds, and cancels their debounces. */
    stopSound: (channel: string) => void;
    /** Loops the sound on the channel, replacing a different sound the channel is looping. */
    startLoopingSound: (soundName: SoundName, channel: string) => void;
    stopLoopingSound: (channel: string) => void;
}

type HowlInstance = InstanceType<typeof Howl>;

interface OneShot {
    howl: HowlInstance;
    debouncedPlay: DebouncedFunc<() => void>;
}

// Bounded by channels x sounds: channels are a fixed set of alerts.
const oneShotsByChannel = new Map<string, Map<SoundName, OneShot>>();
const loopingHowlsByChannel = new Map<string, Map<SoundName, HowlInstance>>();
// Tracked here rather than read from howl.playing(): a Howl that is still loading queues play() and
// reports playing() as false until it loads, so it would start twice and ignore a stop.
const loopingSoundByChannel = new Map<string, SoundName>();

function getOrCreate<V>(map: Map<string, Map<SoundName, V>>, channel: string) {
    let byName = map.get(channel);
    if (!byName) {
        byName = new Map();
        map.set(channel, byName);
    }
    return byName;
}

function getOneShot(soundName: SoundName, channel: string): OneShot {
    const oneShots = getOrCreate(oneShotsByChannel, channel);
    let oneShot = oneShots.get(soundName);
    if (!oneShot) {
        const howl = new Howl({ src: [soundSources[soundName]] });
        oneShot = {
            howl,
            debouncedPlay: debounce(
                function playUnlessPlaying() {
                    if (howl.playing()) return;
                    howl.play();
                },
                1000,
                { leading: true, trailing: false }
            ),
        };
        oneShots.set(soundName, oneShot);
    }
    return oneShot;
}

function getLoopingHowl(soundName: SoundName, channel: string): HowlInstance {
    const howls = getOrCreate(loopingHowlsByChannel, channel);
    let howl = howls.get(soundName);
    if (!howl) {
        howl = new Howl({ src: [soundSources[soundName]], loop: true });
        howls.set(soundName, howl);
    }
    return howl;
}

/**
 * Loads a channel's sound ahead of time, so its first play is prompt. Howler caches a decoded file
 * by URL, so a channel's other sounds load instantly once any channel has preloaded them.
 */
export function preloadSound(soundName: SoundName, channel: string, { loop = false } = {}): void {
    if (loop) {
        getLoopingHowl(soundName, channel);
    } else {
        getOneShot(soundName, channel);
    }
}

function playSound(soundName: SoundName, channel: string): void {
    getOneShot(soundName, channel).debouncedPlay();
}

function stopSound(channel: string): void {
    oneShotsByChannel.get(channel)?.forEach(function stopOneShot({ howl, debouncedPlay }) {
        debouncedPlay.cancel();
        if (howl.playing()) {
            howl.stop();
        }
    });
}

function stopLoopingSound(channel: string): void {
    const soundName = loopingSoundByChannel.get(channel);
    if (!soundName) return;

    loopingSoundByChannel.delete(channel);
    // Howler queues stop() on a Howl that is still loading, so this also cancels a queued play.
    loopingHowlsByChannel.get(channel)?.get(soundName)?.stop();
}

function startLoopingSound(soundName: SoundName, channel: string): void {
    const loopingSound = loopingSoundByChannel.get(channel);
    if (loopingSound === soundName) return;
    if (loopingSound) {
        stopLoopingSound(channel);
    }
    loopingSoundByChannel.set(channel, soundName);
    getLoopingHowl(soundName, channel).play();
}

export const soundPlayer: SoundPlayer = {
    playSound,
    stopSound,
    startLoopingSound,
    stopLoopingSound,
};
